import { Injectable, OnDestroy, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { cleDonnees } from '@stockflow/offline-core';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { Biometrie } from './biometrie';
import { CallService } from './call.service';
import { CLE_MARQUE_VERROU, effacerMarqueVerrou } from './marque-verrou';
import { OperationsEnCours } from './operations-en-cours';
import { PinVaultService } from './pin-vault.service';
import { PolitiqueSecurite } from './politique-securite';
import { rouvrirCleDonnees } from './session-coffre';

/** Bornes de la duree d'inactivite, en minutes : ni un verrou qui saute sans cesse, ni une protection vide de sens. */
export const VERROU_MIN_MINUTES = 1;
export const VERROU_MAX_MINUTES = 60;

/** Ramene une duree a la plage permise ; une valeur absente ou illisible vaut la valeur de la configuration. */
export function dureeValide(minutes: unknown, parDefaut: number): number {
  const n = typeof minutes === 'number' ? minutes : Number.NaN;
  if (!Number.isFinite(n)) return clamp(parDefaut);
  return clamp(n);
}

const clamp = (n: number) => Math.min(VERROU_MAX_MINUTES, Math.max(VERROU_MIN_MINUTES, Math.round(n)));

export type ResultatVerrouillage = 'verrouille' | 'en-attente' | 'sans-nip' | 'deja-verrouille';

export type ResultatDeverrouillage =
  | { statut: 'ok' }
  | { statut: 'mauvais-nip'; restants: number }
  | { statut: 'coffre-perdu' };

/** Evenements qui prouvent qu'une personne est la — `pointermove` est limite pour ne pas saturer. */
const EVENEMENTS_ACTIVITE = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
const PERIODE_VERIFICATION_MS = 10_000;
const LIMITE_POINTEUR_MS = 1_000;

/**
 * LE VERROU DU POSTE.
 *
 * Verrouiller ferme la cle de donnees (ce qu'elle chiffre redevient illisible) et pose un ecran
 * plein ecran qui n'accepte que le NIP. Rien n'est detruit : le panier, l'appel en cours et la file
 * des ventes restent intacts derriere l'ecran.
 *
 * ── LA MARQUE SURVIT AU RECHARGEMENT ──
 * La session se reprend toute seule au demarrage (jeton de rafraichissement) : sans marque, un
 * rechargement de la page contournerait le verrou. La marque est ecrite AVANT de verrouiller et ne
 * s'efface que par un NIP correct, une deconnexion, ou la mort du coffre.
 *
 * ── L'INACTIVITE SE MESURE PAR HORODATAGE ──
 * Pas par un compte a rebours : un onglet endormi ou un ordinateur en veille ne rallonge pas le
 * delai. A chaque verification (toutes les 10 s, et au retour de l'onglet) on compare « maintenant »
 * a la derniere activite.
 *
 * ── CE QUI NE VERROUILLE PAS, OU PAS TOUT DE SUITE ──
 *  - un appel en cours : on parle sans toucher l'ecran, ce n'est pas de l'inactivite ;
 *  - une vente ou un envoi en cours (`OperationsEnCours`) : le verrou attend sa fin, sinon il couperait un
 *    paiement a mi-chemin.
 *
 * ── LIMITE ASSUMEE ──
 * Un poste verrouille protege ce qu'il y a A L'ECRAN et la cle de donnees. Il ne protege pas de la
 * console du navigateur d'un poste DEVERROUILLE : voir `CleDonnees`.
 */
@Injectable({ providedIn: 'root' })
export class VerrouPoste implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly coffre = inject(PinVaultService);
  private readonly appels = inject(CallService);
  private readonly operations = inject(OperationsEnCours);
  private readonly politique = inject(PolitiqueSecurite);
  private readonly biometrie = inject(Biometrie);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _verrouille = signal(false);
  readonly verrouille = this._verrouille.asReadonly();

  private readonly _attente = signal(false);
  /** Vrai quand un verrou est demande mais attend la fin d'une operation. */
  readonly enAttente = this._attente.asReadonly();

  /**
   * Minutes d'inactivite avant verrouillage : le choix de l'entreprise (`PolitiqueSecurite`) s'il existe,
   * sinon la valeur du fichier de configuration. Toujours ramenee a la plage permise.
   */
  readonly dureeMinutes = computed(() =>
    dureeValide(this.politique.dureeInactiviteMinutes(), dureeValide(environment.verrouInactiviteMinutes, 5)),
  );

  readonly occupe = this.operations.occupe;

  constructor() {
    // Un verrou demande pendant une vente se pose des qu'elle est finie.
    effect(() => {
      if (this.operations.compte() === 0 && this._attente()) {
        this._attente.set(false);
        void this.verrouiller();
      }
    });
    // Une session qui se ferme emporte le verrou et le choix de l'entreprise : la prochaine personne
    // n'herite ni d'un ecran verrouille ni de la duree d'une autre entreprise.
    effect(() => {
      if (!this.auth.isAuthenticated()) {
        this._verrouille.set(false);
        this.politique.definirDureeInactivite(null);
      }
    });
  }

  private derniereActivite = Date.now();
  private derniereMesurePointeur = 0;
  private minuterie: ReturnType<typeof setInterval> | null = null;
  private demarre = false;

  /** Branche la detection d'activite et relit la marque d'un verrou precedent. A appeler une fois, depuis le layout. */
  demarrer(): void {
    if (!this.isBrowser || this.demarre) return;
    this.demarre = true;

    this.reprendreMarque();
    this.derniereActivite = Date.now();

    for (const e of EVENEMENTS_ACTIVITE) window.addEventListener(e, this.surActivite, { passive: true, capture: true });
    window.addEventListener('pointermove', this.surPointeur, { passive: true, capture: true });
    document.addEventListener('visibilitychange', this.surVisibilite);
    window.addEventListener('keydown', this.surRaccourci, true);
    this.minuterie = setInterval(() => this.verifierInactivite(), PERIODE_VERIFICATION_MS);
  }

  ngOnDestroy(): void {
    this.arreter();
  }

  arreter(): void {
    if (!this.demarre) return;
    this.demarre = false;
    for (const e of EVENEMENTS_ACTIVITE) window.removeEventListener(e, this.surActivite, true);
    window.removeEventListener('pointermove', this.surPointeur, true);
    document.removeEventListener('visibilitychange', this.surVisibilite);
    window.removeEventListener('keydown', this.surRaccourci, true);
    if (this.minuterie !== null) clearInterval(this.minuterie);
    this.minuterie = null;
  }

  private readonly surActivite = () => {
    if (!this._verrouille()) this.derniereActivite = Date.now();
  };

  private readonly surPointeur = () => {
    const maintenant = Date.now();
    if (maintenant - this.derniereMesurePointeur < LIMITE_POINTEUR_MS) return;
    this.derniereMesurePointeur = maintenant;
    this.surActivite();
  };

  private readonly surVisibilite = () => {
    if (document.visibilityState === 'visible') this.verifierInactivite();
  };

  /** Ctrl + Alt + L : verrouille tout de suite. Aucun navigateur ne reserve cette combinaison. */
  private readonly surRaccourci = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.altKey && !e.metaKey && !e.shiftKey && e.key.toLowerCase() === 'l') {
      e.preventDefault();
      void this.verrouiller();
    }
  };

  /** Verrouille apres inactivite, sauf appel en cours ou verrou deja pose. */
  verifierInactivite(): void {
    if (this._verrouille() || !this.auth.isAuthenticated()) return;
    if (this.appels.activeCall()) {
      this.derniereActivite = Date.now();
      return;
    }
    if (Date.now() - this.derniereActivite >= this.dureeMinutes() * 60_000) void this.verrouiller();
  }

  async verrouiller(): Promise<ResultatVerrouillage> {
    if (this._verrouille()) return 'deja-verrouille';
    const email = this.auth.currentUser()?.email;
    // Sans NIP il n'y a rien pour deverrouiller : verrouiller enfermerait l'utilisateur dehors.
    if (!email || !(await this.coffre.hasVault(email))) return 'sans-nip';
    if (this.operations.occupe()) {
      this._attente.set(true);
      return 'en-attente';
    }
    this._attente.set(false);
    // La marque d'abord : si la page plante entre les deux, le poste reste verrouille au rechargement.
    try {
      localStorage.setItem(CLE_MARQUE_VERROU, email);
    } catch {
      /* stockage inaccessible : le verrou tient quand meme jusqu'au rechargement */
    }
    cleDonnees.verrouiller();
    this._verrouille.set(true);
    return 'verrouille';
  }

  /** Un NIP correct rouvre la cle ; trois mauvais efface le coffre (meme regle qu'a la connexion). */
  async deverrouiller(pin: string): Promise<ResultatDeverrouillage> {
    const email = this.auth.currentUser()?.email;
    if (!email) return { statut: 'coffre-perdu' };

    const resultat = await this.coffre.unlock(email, pin);
    if (!resultat.data) {
      if (resultat.remaining <= 0) {
        // Le coffre est efface : plus rien ne deverrouillera ce poste. L'ecran deconnecte ; le verrou
        // reste pose jusque-la (voir l'effet du constructeur) pour ne rien laisser entrevoir.
        effacerMarqueVerrou();
        return { statut: 'coffre-perdu' };
      }
      return { statut: 'mauvais-nip', restants: resultat.remaining };
    }

    // Un coffre d'avant la cle de donnees ne la rouvre pas : le NIP est bon, le poste se deverrouille,
    // la cle reste fermee jusqu'au prochain NIP saisi en ligne.
    await rouvrirCleDonnees(resultat.data);
    cleDonnees.oublierBrute();
    effacerMarqueVerrou();
    this.derniereActivite = Date.now();
    this._verrouille.set(false);
    return { statut: 'ok' };
  }

  /**
   * Un geste biometrique rouvre la cle de donnees (copie rangee dans le coffre, voir `Biometrie`).
   *
   * Ne touche JAMAIS au compteur d'essais du NIP : refuser une empreinte n'est pas un mauvais NIP, et
   * trois lectures ratees ne doivent pas effacer le coffre. Meme resultat qu'un NIP correct : marque
   * effacee, inactivite repartie de zero.
   */
  async deverrouillerParBiometrie(): Promise<'ok' | 'annule' | 'echec' | 'indisponible'> {
    const email = this.auth.currentUser()?.email;
    if (!email || !this._verrouille() || !(await this.coffre.hasVault(email))) return 'indisponible';

    const r = await this.biometrie.deverrouiller(email);
    if (r.statut !== 'ok') return r.statut;
    try {
      await cleDonnees.ouvrir(r.cleBrute);
    } catch {
      return 'echec';
    }
    // Le coffre de cette personne devient celui dont la mort referme la cle, comme apres un NIP.
    this.coffre.declarerDeverrouille(email);
    cleDonnees.oublierBrute();
    effacerMarqueVerrou();
    this.derniereActivite = Date.now();
    this._verrouille.set(false);
    return 'ok';
  }

  /** Relit la marque laissee par un verrou avant un rechargement. */
  private reprendreMarque(): void {
    let marque: string | null = null;
    try {
      marque = localStorage.getItem(CLE_MARQUE_VERROU);
    } catch {
      return;
    }
    if (!marque) return;
    const email = this.auth.currentUser()?.email;
    // La marque d'une autre personne ne verrouille pas celle-ci : elle est effacee avec sa session.
    if (email && marque !== email) {
      effacerMarqueVerrou();
      return;
    }
    cleDonnees.verrouiller();
    this._verrouille.set(true);
  }
}
